import { EMOTION_META, STANCE_META } from '../../../constants/sentiment';

/**
 * A run's standing in one table cell: the emotion that dominated, and how many
 * were with the account versus against it.
 *
 * Reads run.totals, snapshotted when a run finishes. Stance is used when the
 * run has it; an older run not yet relabelled falls back to polarity, which
 * could not tell an ally's anger from an attacker's - so the fallback is
 * labelled as the weaker reading it is.
 */
export function MoodCell({ totals, status }) {
	const withStance = (totals?.for || 0) + (totals?.against || 0) + (totals?.noStance || 0);
	const labelled = (totals?.positive || 0) + (totals?.negative || 0) + (totals?.neutral || 0);

	if (!withStance && !labelled) {
		// A run still working, or one that heard nothing, must not render as a
		// neutral grey bar - that reads as a measured result rather than an
		// absent one.
		return <span className="text-sm text-muted-foreground">{status === 'failed' ? '-' : 'Not read yet'}</span>;
	}

	const meta = EMOTION_META[totals.dominantEmotion] || EMOTION_META.neutral;

	// Stance: with / none / against, in that order so "against" sits at the
	// right edge where the eye lands last, as the number to act on.
	const useStance = withStance > 0;
	const seg = useStance
		? [
			{ n: totals.for || 0, cls: STANCE_META.for.bg },
			{ n: totals.noStance || 0, cls: STANCE_META.none.bg },
			{ n: totals.against || 0, cls: STANCE_META.against.bg },
		]
		: [
			{ n: totals.positive || 0, cls: 'bg-teal-500' },
			{ n: totals.neutral || 0, cls: 'bg-input' },
			{ n: totals.negative || 0, cls: 'bg-rose-600' },
		];
	const base = useStance ? withStance : labelled;
	const pct = (n) => `${Math.round((n / base) * 100)}%`;
	const title = useStance
		? `${totals.for || 0} with you · ${totals.noStance || 0} no position · ${totals.against || 0} against you`
		: `${totals.positive || 0} positive · ${totals.neutral || 0} neutral · ${totals.negative || 0} negative (before stance was read)`;

	return (
		<div className="min-w-[10rem]">
			<div className="flex items-center gap-1.5">
				<span className={`inline-block h-2 w-2 shrink-0 rounded-sm ${meta.bg}`} />
				<span className="text-sm text-foreground">{meta.label}</span>
			</div>
			<div className="mt-1 flex h-1.5 overflow-hidden rounded-full bg-muted" title={title}>
				{seg.map((s, i) => s.n > 0 && <span key={i} className={s.cls} style={{ width: pct(s.n) }} />)}
			</div>
			<div className="mt-0.5 text-xs text-muted-foreground tabular-nums">
				{useStance
					? <>{pct(totals.for || 0)} with you · <span className={(totals.against || 0) > 0 ? STANCE_META.against.text : ''}>{pct(totals.against || 0)} against</span></>
					: <>{pct(totals.positive || 0)} positive · {pct(totals.negative || 0)} negative</>}
			</div>
		</div>
	);
}
